import { useCallback } from 'react';

import { useNonRevalidatingFetcher } from '~/hooks/useNonRevalidatingFetcher';
import type { CreateGroupSubscriptionSeatReductionActionData } from '~/routes/__actions/create_group_subscription_seat_reduction';
import type { EditGroupSubscriptionSeatReductionActionData } from '~/routes/__actions/edit_group_subscription_seat_reduction';

/**
 * Both seat-reduction endpoints return `{ message }` on success / `{ errors }`
 * on 422, so the FE only needs an `ok` + flattened `error`. There's no row
 * payload — the change is scheduled, not applied, until renewal.
 */
export type SeatReductionResult = {
  ok: boolean;
  error: string | null;
};

export type EditReductionInput = {
  targetSeatCount?: number | null;
  seatIds?: string[];
  cancel?: boolean;
};

export type UseSeatReductionResult = {
  /** POST — schedules a brand-new reduction. `seatIds` must equal the deficit
   *  (or be empty when only unfilled seats are dropped). */
  scheduleReduction: (groupId: string, targetSeatCount: number, seatIds: string[]) => Promise<SeatReductionResult>;
  /** PATCH — modifies/cancels an existing schedule. The BE branches on which
   *  fields are present (cancel / raise / lower / swap). */
  editReduction: (groupId: string, input: EditReductionInput) => Promise<SeatReductionResult>;
};

export function useSeatReduction(): UseSeatReductionResult {
  const scheduleFetcher = useNonRevalidatingFetcher<() => Promise<CreateGroupSubscriptionSeatReductionActionData>>();
  const editFetcher = useNonRevalidatingFetcher<() => Promise<EditGroupSubscriptionSeatReductionActionData>>();

  const scheduleReduction = useCallback(
    async (groupId: string, targetSeatCount: number, seatIds: string[]): Promise<SeatReductionResult> => {
      const submitResult = await scheduleFetcher.submit(
        JSON.stringify({ group_subscription_id: groupId, target_seat_count: targetSeatCount, seat_ids: seatIds }),
        { method: 'post', action: '/create_group_subscription_seat_reduction', encType: 'application/json' },
      );
      const error = submitResult.data?.error ?? (submitResult.ok ? null : 'Failed to schedule seat reduction');
      return { ok: submitResult.ok && !error, error };
    },
    [scheduleFetcher],
  );

  const editReduction = useCallback(
    async (groupId: string, input: EditReductionInput): Promise<SeatReductionResult> => {
      const submitResult = await editFetcher.submit(
        JSON.stringify({
          group_subscription_id: groupId,
          target_seat_count: input.targetSeatCount ?? null,
          seat_ids: input.seatIds,
          cancel: input.cancel ?? false,
        }),
        { method: 'post', action: '/edit_group_subscription_seat_reduction', encType: 'application/json' },
      );
      const error = submitResult.data?.error ?? (submitResult.ok ? null : 'Failed to update seat reduction');
      return { ok: submitResult.ok && !error, error };
    },
    [editFetcher],
  );

  return { scheduleReduction, editReduction };
}
